/**
 * apps/web :: components/trial/TrialCountdownBanner
 *
 * Persistent header band shown during the LAST 6 HOURS of a free trial
 * (ROADS-445 FE-01). Live updating countdown ("Free trial — 5h 23m
 * left"); dismissible per session, not permanently.
 *
 * Design rules:
 *   - Reduced-motion aware: never spin, never slide. The countdown is
 *     pure text — `prefers-reduced-motion: reduce` users still see the
 *     same updating text content (text changes are not motion under
 *     WCAG 2.3.3).
 *   - Accessible via `aria-live="polite"` so screen readers announce
 *     the remaining minutes when they roll over (without interrupting
 *     other speech).
 *   - "Dismiss for this session" button stores the dismissal in
 *     sessionStorage; reload-as-tab clears it (per spec).
 *   - Server-clock-driven: the countdown is computed from
 *     `expires_at_ms` against the local clock, but we ALSO recompute
 *     against `now_ms` from the server response so a clock-skewed user
 *     sees an honest count from page load. After that, we tick locally
 *     using `Date.now()` — the worst case is a few minutes of drift
 *     during a 6-hour banner window, which is fine. The server-side
 *     truth is re-verified on every API call to a paid surface.
 *
 * NOT shown when:
 *   - Trial is already inactive (paywall handles that surface).
 *   - More than 6 hours remain (no banner; learner has plenty of trial
 *     left and a permanent banner would be noise).
 *   - User dismissed the banner this session.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import {
  TRIAL_BANNER_THRESHOLD_MS,
  type TrialStatus,
} from "@/lib/trial-api";

const DISMISSED_STORAGE_KEY = "rb:trial-countdown-banner-dismissed:v1";

function readDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(DISMISSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeDismissed(value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(DISMISSED_STORAGE_KEY, String(value));
  } catch {
    // sessionStorage may be unavailable (private browsing) — banner
    // still dismisses for the rest of the page mount, just won't
    // persist across navigations.
  }
}

export interface TrialCountdownBannerProps {
  status: TrialStatus;
  /**
   * Where the "View plans" CTA navigates. Defaults to a sentinel `null`
   * which renders the banner without a CTA — used by tests + the
   * paywall surface itself.
   */
  ctaHref?: string | null;
  /** Test seam: drives the polling interval. Default 30s. */
  tickIntervalMs?: number;
  /** Test seam: now-provider so the spec doesn't need real timers. */
  nowProvider?: () => number;
}

/**
 * Format a positive milliseconds delta as "Xh Ym". Truncates to whole
 * minutes to keep the banner stable; sub-minute precision would
 * re-render every second and burn battery.
 */
export function formatTimeLeft(remainingMs: number): { hours: number; minutes: number } {
  const totalMinutes = Math.max(0, Math.floor(remainingMs / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
}

const DEFAULT_TICK_MS = 30_000;

export default function TrialCountdownBanner({
  status,
  ctaHref = null,
  tickIntervalMs = DEFAULT_TICK_MS,
  nowProvider = () => Date.now(),
}: TrialCountdownBannerProps): JSX.Element | null {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());
  // Tick triggers a re-render every `tickIntervalMs` so the displayed
  // hh/mm refreshes. We intentionally don't store `now` in state — we
  // derive it inline below so the value used in JSX is the SAME one used
  // in the should-render guard, eliminating a stale-closure off-by-one.
  const [, setTickIndex] = useState(0);

  useEffect(() => {
    if (dismissed) return;
    const id = window.setInterval(() => {
      setTickIndex((n) => n + 1);
    }, tickIntervalMs);
    return () => window.clearInterval(id);
  }, [dismissed, tickIntervalMs]);

  const now = nowProvider();
  const remainingMs = status.expires_at_ms - now;
  const { hours, minutes } = useMemo(() => formatTimeLeft(remainingMs), [remainingMs]);

  // Render the banner only when the trial is still active AND inside
  // the 6-hour threshold. Outside this window we render nothing (so
  // the layout doesn't reserve space).
  if (!status.active) return null;
  if (remainingMs <= 0) return null;
  if (remainingMs > TRIAL_BANNER_THRESHOLD_MS) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    writeDismissed(true);
    setDismissed(true);
  };

  const label = t("paywall.trialBanner.label", {
    hours,
    minutes,
    defaultValue: "Free trial — {{hours}}h {{minutes}}m left",
  });

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="trial-countdown-banner"
      className="bg-amber-50 border-b border-amber-200 text-amber-900 px-md py-sm flex items-center justify-between gap-md text-sm"
    >
      <div className="flex items-center gap-sm font-medium">
        <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full bg-amber-500" />
        <span data-testid="trial-countdown-text">{label}</span>
      </div>
      <div className="flex items-center gap-sm">
        {ctaHref ? (
          <a
            href={ctaHref}
            className="text-amber-900 underline font-semibold hover:text-amber-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700"
          >
            {t("paywall.trialBanner.viewPlans", { defaultValue: "View plans" })}
          </a>
        ) : null}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t("paywall.trialBanner.dismiss", { defaultValue: "Dismiss free-trial banner" })}
          className="p-1 rounded-md hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700"
        >
          <X aria-hidden="true" size={16} />
        </button>
      </div>
    </div>
  );
}
